import { useCallback, useRef, useState } from 'react';

type Options = {
  /** 上传一个文件，回它的最终地址。调用方各自传自己那条链路的 API 函数 */
  upload: (file: File) => Promise<{ url: string }>;
  /** 正文的 setter，用法与 useState 的函数式更新一致 */
  setValue: (updater: (prev: string) => string) => void;
  /** 正在编辑的 textarea，用来取光标位置。没有就插到末尾 */
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  onError: (message: string | null) => void;
};

// 往一个 markdown 输入框里粘 / 拖 / 选图片。留言和任务备注共用这一份。
//
// 抽出来是因为它有两个调用方了，而这段逻辑有两个不显然的地方，复制一份必然
// 复制出 bug（其中第二个就是复制式实现踩过的）：
//
// 1. 先在光标处插占位、上传完再替换。不这么做只有两个更差的选择：锁住输入框等
//    上传（一张 2MB 截图在慢网络上要几秒，期间不能打字）；或者上传完往「当前
//    光标位置」插 —— 而那时光标早移到别处了，图片会插进句子中间（spec §6.5）。
// 2. 占位串必须唯一。曾用 Date.now() 生成，而同一次粘贴的多张图落在同一毫秒 ——
//    占位串完全相同，而回填是按字符串 replace 的，于是两张图互抢对方的占位
//    （先传完的替换掉排在前面的那个），其中一张失败时错误标记也落错位置。
// 3. upload 是注入的，不是在这里 import api：留言 / 备注挂在 task 上，wiki 正文
//    挂在页面上，两条链路的端点不同。把「传给谁」留给调用方，这个 hook 就只剩
//    「往 markdown 输入框里插图」这一件事 —— 而上面那两个坑正是这件事的坑。
export default function useMarkdownImages({ upload, setValue, textareaRef, onError }: Options) {
  const [uploading, setUploading] = useState(0);
  const seq = useRef(0);

  // 内部这个叫 runUpload 而不是 upload：后者现在是入参，同名会遮蔽它。
  const runUpload = useCallback(
    async (file: File, token: string) => {
      setUploading((n) => n + 1);
      onError(null);

      try {
        const { url } = await upload(file);
        setValue((prev) => prev.replace(token, `![${file.name}](${url})`));
      } catch (e) {
        // 占位换成一行可见的提示，由人决定删掉还是重试 —— 静默移除会让人
        // 以为图片插进去了。
        setValue((prev) => prev.replace(token, '`[图片上传失败]`'));
        onError(e instanceof Error ? e.message : '图片传不上去');
      } finally {
        setUploading((n) => n - 1);
      }
    },
    [upload, setValue, onError]
  );

  const takeFiles = useCallback(
    (files: FileList | null) => {
      const images = Array.from(files ?? []).filter((file) => file.type.startsWith('image/'));
      if (images.length === 0) return;

      const pending = images.map((file) => ({
        file,
        token: `![上传中…${(seq.current += 1)}]()`,
      }));

      // 所有占位在一次 setValue 里按顺序插入。每个文件各调一次会让它们读到
      // 同一个 selectionStart，于是后一张插到前一张前面 —— 顺序反过来。
      // at 在 updater 里对 prev.length 收口，免得光标位置比当前正文还长。
      const el = textareaRef.current;
      setValue((prev) => {
        const at = el ? Math.min(el.selectionStart, prev.length) : prev.length;
        const block = pending.map((item) => item.token).join('\n');
        return `${prev.slice(0, at)}${block}${prev.slice(at)}`;
      });

      pending.forEach(({ file, token }) => void runUpload(file, token));
    },
    [runUpload, setValue, textareaRef]
  );

  // 给输入框直接摊开用：<textarea {...imagePasteHandlers} />
  const handlers = {
    onPaste: (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (event.clipboardData.files.length === 0) return;
      event.preventDefault();
      takeFiles(event.clipboardData.files);
    },
    onDrop: (event: React.DragEvent<HTMLTextAreaElement>) => {
      if (event.dataTransfer.files.length === 0) return;
      event.preventDefault();
      takeFiles(event.dataTransfer.files);
    },
  };

  return { takeFiles, uploading, handlers };
}
